import React, { useState } from "react";
import { Send, Loader2, Mail, AlertTriangle } from "lucide-react";
import { PageHero } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { Honeypot } from "@/components/Honeypot";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { IMG } from "@/lib/images";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", hp: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) { toast.error("Please fill in every field."); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you for reaching out. We'll respond with care.");
      setForm({ name: "", email: "", subject: "", message: "", hp: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title="We'd be honored to hear from you"
        subtitle="For partnerships, speaking requests, feedback, or simply to say hello. For emergencies, please use the crisis lines above."
        image={IMG.lakeDock}
        testid="contact-hero"
      />

      <section className="max-w-3xl mx-auto px-4 py-16 sm:py-20">
        <Reveal>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 mb-8" data-testid="contact-crisis-notice">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900 leading-relaxed">
              This form is not monitored for emergencies. If you or someone you love is in crisis, call or text
              <strong> 988</strong> now, or <strong>911</strong> if there is immediate danger.
            </p>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <form onSubmit={submit} className="bg-white rounded-2xl p-6 sm:p-8 card-tactile space-y-4" data-testid="contact-form">
            <Honeypot value={form.hp} onChange={(v) => setForm({ ...form, hp: v })} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
              <Input type="email" placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
            </div>
            <Input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject-input" />
            <Textarea rows={6} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
            <Button type="submit" disabled={loading} className="w-full btn-gold font-semibold h-12" data-testid="contact-submit-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
            </Button>
          </form>
        </Reveal>

        <p className="text-center text-sm text-bsn-sapphire/70 mt-8 flex items-center justify-center gap-2">
          <Mail className="w-4 h-4" />
          <button type="button" onClick={() => { window.location.href = `mailto:${["BrightStoriesNetwork", "protonmail.com"].join("@")}`; }} className="underline-offset-2 hover:underline" data-testid="contact-email-link">
            BrightStoriesNetwork<span aria-hidden="true">&#64;</span>protonmail<span>.</span>com
          </button>
        </p>
      </section>
    </div>
  );
}
